import * as React from "react";
import {
  Link,
  createFileRoute,
  notFound,
  useNavigate,
} from "@tanstack/react-router";
import * as z from "zod";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PlusIcon, TrashIcon } from "lucide-react";
import { HTTPError } from "ky";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import type { UserPolicyAssociation } from "@/lib/queries/users";
import type { Policy } from "@/lib/queries/policies";
import type { ApiRequestDescriptor } from "@/lib/policy-conflicts";
import { BreadcrumbNav } from "@/components/ui/breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/http";
import { GenericTable } from "@/components/generic-table";
import { useSet } from "@/lib/hooks";
import {
  useUserPoliciesList,
  userPoliciesListQueryOptions,
  userQueryOptions,
} from "@/lib/queries/users";
import { usePoliciesList } from "@/lib/queries/policies";
import { runConflictAwareSequence } from "@/lib/policy-conflicts";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import { Actions } from "@/components/ui/actions";
import { useResourceAccessPermission } from "@/lib/perms";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";

const pathParamsSchema = z.object({
  id: z.uuid(),
});

export const Route = createFileRoute("/_app/users_/$id")({
  component: RouteComponent,
  params: {
    parse: pathParamsSchema.parse,
  },
  async loader({ context: { queryClient }, params: { id } }) {
    queryClient.ensureQueryData(userPoliciesListQueryOptions(id));

    try {
      await queryClient.ensureQueryData(userQueryOptions(id));
    } catch (e) {
      if (!(e instanceof HTTPError)) throw e;
      if (e.response.status === 404) throw notFound();
      throw e;
    }
  },
  pendingComponent: () => (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-64 w-full" />
    </div>
  ),
  staticData: {
    permissionGuards: [
      {
        resource: "users",
        actions: ["read"],
      },
    ],
  },
});

function RouteComponent() {
  const { id: userId } = Route.useParams();

  const userDetail = useSuspenseQuery(userQueryOptions(userId));

  return (
    <div className="space-y-6 p-6">
      <BreadcrumbNav
        items={[
          { label: "Users", link: <Link to="/users">Users</Link> },
          { label: "User Details" },
        ]}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Name
                </label>
                <p className="mt-1 text-lg font-semibold">
                  {userDetail.data.full_name}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Email
                </label>
                <p className="mt-1 text-lg font-semibold">
                  {userDetail.data.email}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Status
                </label>
                <div className="mt-1">
                  <StatusBadge
                    variant={userDetail.data.is_active ? "success" : "default"}
                  >
                    {userDetail.data.is_active ? "Active" : "Inactive"}
                  </StatusBadge>
                </div>
              </div>
              {userDetail.data.role && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Role
                  </label>
                  <p className="mt-1 text-lg font-semibold">
                    {userDetail.data.role.name}
                  </p>
                </div>
              )}

              {userDetail.data.department && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Department
                  </label>
                  <p className="mt-1 text-lg font-semibold">
                    {userDetail.data.department.name}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Created At
                </label>
                <p className="mt-1">
                  {new Date(userDetail.data.created_at).toLocaleString()}
                </p>
              </div>
              {userDetail.data.updated_at && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Updated At
                  </label>
                  <p className="mt-1">
                    {new Date(userDetail.data.updated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <UserPoliciesTable />
    </div>
  );
}

const columns = [
  {
    accessorKey: "name",
    header: "Name",
    size: 300,
  },
  {
    accessorKey: "priority",
    header: "Priority",
    size: 20,
  },
  {
    accessorKey: "type",
    header: "Type",
    size: 80,
    cell: ({ row: { original } }) => {
      return (
        <StatusBadge
          variant={original.type === "Blocklist" ? "error" : "warning"}
        >
          {original.type}
        </StatusBadge>
      );
    },
  },

  {
    id: "status",
    header: "Status",
    size: 80,
    cell: ({ row: { original } }) => {
      return (
        <div className="flex gap-2">
          {original.is_active && (
            <StatusBadge variant="success">Active</StatusBadge>
          )}
          {original.is_pre_ready && (
            <StatusBadge variant="info">Pre-Ready</StatusBadge>
          )}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canRemove = useResourceAccessPermission("policy_users", ["remove"]);

      return (
        <Actions>
          {canRemove.data && <DeleteDialog policy={original} />}
        </Actions>
      );
    },
  },
] as const satisfies Array<ColumnDef<UserPolicyAssociation>>;
function UserPoliciesTable() {
  const { id: userId } = Route.useParams();
  const userPolicies = useUserPoliciesList(userId);

  const canAdd = useResourceAccessPermission("policy_users", ["add"]);
  const canRemove = useResourceAccessPermission("policy_users", ["remove"]);

  const table = useReactTable({
    data: userPolicies.data ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnVisibility: {
        actions: Boolean(canRemove.data),
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Policies</h2>
        {canAdd.data && (
          <AddPoliciesDialog
            existingPolicyIds={userPolicies.data?.map((p) => p.id) ?? []}
          />
        )}
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={userPolicies.isLoading} />
      </div>
    </div>
  );
}

function AddPoliciesDialog({
  existingPolicyIds,
}: {
  existingPolicyIds: Array<number>;
}) {
  const { id: userId } = Route.useParams();

  const [open, setOpen] = React.useState(false);

  const existingPolicyIdsSet = React.useMemo(
    () => new Set(existingPolicyIds),
    [existingPolicyIds],
  );

  const selectedPolicies = useSet<number>();

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const policies = usePoliciesList({
    page: pagination.pageIndex + 1,
    per_page: pagination.pageSize,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const policyNameById = React.useMemo(() => {
    const map = new Map<number, string>();
    for (const p of policies.data?.items ?? []) map.set(p.id, p.name);
    return map;
  }, [policies.data]);

  const addMutation = useMutation({
    async mutationFn({
      userId,
      policyIds,
    }: {
      userId: string;
      policyIds: Array<number>;
    }) {
      const requests: Array<ApiRequestDescriptor> = policyIds.map(
        (policyId) => ({
          method: "POST",
          url: `api/v1/policies/${policyId}/users`,
          payload: { user_id: userId },
          kind: "add-user-to-policy",
          label: policyNameById.get(policyId) ?? `Policy #${policyId}`,
        }),
      );

      return runConflictAwareSequence(requests, {
        returnUrl: window.location.pathname + window.location.search,
        successToast: `${policyIds.length} polic${
          policyIds.length === 1 ? "y" : "ies"
        } added successfully`,
      });
    },
    onSuccess: (result, variables) => {
      if (result.status === "conflict") {
        setOpen(false);
        selectedPolicies.clear();
        navigate({ to: "/resolve-conflicts" });
        return;
      }

      queryClient.invalidateQueries({
        queryKey: ["users", variables.userId, "policies", "list"],
      });

      toast.success(
        `${variables.policyIds.length} polic${
          variables.policyIds.length === 1 ? "y" : "ies"
        } added successfully`,
      );
      setOpen(false);
      selectedPolicies.clear();
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body?.detail) {
          message =
            typeof body.detail === "string"
              ? body.detail
              : (body.detail.message ?? message);
        }
      }
      toast.error(`Failed to add policies: ${message}`);
    },
  });

  const addPoliciesColumns = React.useMemo(
    () =>
      [
        {
          id: "select",
          header: ({ table }) => {
            const selectableRows = table
              .getRowModel()
              .rows.filter((r) => !existingPolicyIdsSet.has(r.original.id));
            const selectedSelectableCount = selectableRows.filter((r) =>
              selectedPolicies.has(r.original.id),
            ).length;

            const isAllSelected =
              selectableRows.length > 0 &&
              selectedSelectableCount === selectableRows.length;
            const isSomeSelected =
              selectedSelectableCount > 0 &&
              selectedSelectableCount < selectableRows.length;

            return (
              <Checkbox
                checked={
                  isAllSelected
                    ? true
                    : isSomeSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={(value) => {
                  if (value) {
                    selectableRows.forEach((r) =>
                      selectedPolicies.add(r.original.id),
                    );
                  } else {
                    selectableRows.forEach((r) =>
                      selectedPolicies.delete(r.original.id),
                    );
                  }
                }}
                aria-label="Select all"
              />
            );
          },
          cell: ({ row }) => {
            const isExisting = existingPolicyIdsSet.has(row.original.id);
            const isChecked =
              isExisting || selectedPolicies.has(row.original.id);

            return (
              <Checkbox
                checked={isChecked}
                disabled={isExisting}
                onCheckedChange={(value) => {
                  if (isExisting) return;

                  if (value) {
                    selectedPolicies.add(row.original.id);
                  } else {
                    selectedPolicies.delete(row.original.id);
                  }
                }}
                aria-label="Select row"
              />
            );
          },
        },
        {
          header: "Name",
          accessorKey: "name",
          cell: ({ row }) => {
            const isExisting = existingPolicyIdsSet.has(row.original.id);
            return (
              <div className="flex items-center gap-2">
                <span>{row.original.name}</span>
                {isExisting && (
                  <Badge variant="secondary" className="text-xs">
                    Already Added
                  </Badge>
                )}
              </div>
            );
          },
        },
        {
          header: "Priority",
          accessorKey: "priority",
        },
        {
          header: "Type",
          accessorKey: "type",
          cell: ({ row }) => {
            return (
              <Badge
                variant="outline"
                className={
                  row.original.type === "Blocklist"
                    ? "bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-100"
                    : "bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-100"
                }
              >
                {row.original.type}
              </Badge>
            );
          },
        },
        {
          header: "Status",
          id: "status",
          cell: ({ row }) => {
            const policy = row.original;
            return (
              <div className="flex gap-2">
                {policy.is_active && (
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-100"
                  >
                    Active
                  </Badge>
                )}
                {policy.is_pre_ready && (
                  <Badge
                    variant="outline"
                    className="bg-purple-50 text-purple-700 dark:bg-purple-900 dark:text-purple-100"
                  >
                    Pre-Ready
                  </Badge>
                )}
              </div>
            );
          },
        },
      ] as const satisfies Array<ColumnDef<Policy>>,
    [existingPolicyIdsSet, selectedPolicies],
  );

  const table = useReactTable({
    data: policies.data?.items ?? [],
    columns: addPoliciesColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination,
    },
    onPaginationChange: setPagination,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    rowCount: policies.data?.count ?? 1,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const newSelectionCount = selectedPolicies.size;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon />
          Add Policies
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] max-w-6xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Add Policies</DialogTitle>
          <DialogDescription>
            Select policies to add to this user. ({newSelectionCount} new
            policy(ies) selected.)
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          <div className="rounded-md border">
            <GenericTable table={table} isLoading={policies.isLoading} />
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {pagination.pageIndex + 1} of {table.getPageCount() || 1}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button
            variant="outline"
            onClick={() => {
              selectedPolicies.clear();
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              addMutation.mutate({
                userId,
                policyIds: Array.from(selectedPolicies),
              })
            }
            disabled={newSelectionCount === 0 || addMutation.isPending}
          >
            {addMutation.isPending
              ? "Adding..."
              : `Add ${newSelectionCount} Policy(ies)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ policy }: { policy: UserPolicyAssociation }) {
  const { id: userId } = Route.useParams();

  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      policyId,
      userId,
    }: {
      policyId: number;
      userId: string;
    }) => {
      return api.raw.delete(`api/v1/policies/${policyId}/users/${userId}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["users", variables.userId, "policies", "list"],
      });

      setOpen(false);
      toast.success(`Policy "${policy.name}" removed successfully`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
        >
          <TrashIcon className="size-5" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Policy from User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove the policy "{policy.name}"?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending} asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>

          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ policyId: policy.id, userId })}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
